import { stringify } from 'querystring'

import { trackEvent } from '@patrianna/analytics'
import {
  openDialog,
  closeLatestDialog,
  cleanDialogStack,
  getDialogVisibilityByName,
  replaceDialog,
  getDialogStack,
  removeShownSessionDialog,
  getShownSessionDialogs,
} from '@patrianna/dialog-manager'
import type { FreeSpinType } from '@patrianna/shared-patrianna-types/store/FreeSpinModule'
import type { GameProduct, GameProductShort } from '@patrianna/shared-patrianna-types/store/GameModule'
import type {
  GetAccountFreeSpinsRequest,
  LowOnCoinsRequest,
} from '@patrianna/shared-patrianna-types/websocket/requests'
import type {
  GetAccountFreeSpinsResponse,
  LowOnCoinsResponse,
} from '@patrianna/shared-patrianna-types/websocket/response'
import { detectMobile } from '@patrianna/shared-utils/helpers'
import { skipOtpSelectorWithMandatoryAndClosed } from '@patrianna-payments/shared-store/skip-otp'
import type { useRouter } from 'next/navigation'

import ROUTES from 'config/routes.json'
import getAccountBonusesRequest from 'services/gateway/requests/getAccountBonuses'
import type { TypedThunk } from 'src/store/types'
import { getGameByRoute, isGameProductErrorResponse } from 'src/utils/loadGameInfoGetInitialProps'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getOffers } from 'store/modules/shop/actions'
import { getUserRestrictionsSelector, isGuestModeSelector, isLoggedInSelector } from 'store/modules/user/selectors'
import type { SupportedCurrencies } from 'types/currencies'
import {
  PROCESSED_FREE_SPIN_IDS,
  getItemFormLocalStorageSelector,
  removeDataFromLocalStorage,
  setDataToLocalStorage,
} from 'utils/localStorage'
import { INTERNAL_GAME_NAVIGATION, setDataToSessionStorage } from 'utils/sessionStorage'

import { setActiveCurrency } from '../currencies/actions'
import { getActiveCurrencyIdSelector } from '../currencies/selectors'
import { getFirstUnlockedSlotSelector } from '../games/selectors'
import { isDiscountOffersAvailable } from '../shop/selectors'

import { actions } from './slice'

export const {
  setActiveGameCode,
  clearSlotGameFlow,
  saveFreeSpins,
  setGoldMinBet,
  setSweepstakeMinBet,
  setLowOnCoinsLastDateOpened,
  setFreeSpinsLoaded,
} = actions

const openBingoGame = (
  currency: SupportedCurrencies,
  routerPush: ReturnType<typeof useRouter>['push'],
  gameRoomId?: number | string,
  options?: { [key: string]: string | number },
) => {
  const isMobile = detectMobile()
  const roomId =
    gameRoomId && currency === 'GC' && !gameRoomId.toString().includes('-gc') ? `${gameRoomId}-gc` : gameRoomId
  const roomUrl = roomId ? `/rooms/${roomId}` : ''
  const url = `/bingo${roomUrl}/${currency === 'SC' ? 'sweepstake/' : ''}play/${isMobile ? 'mobile' : ''}`
  const query = options && Object.keys(options).length > 0 ? `?${stringify(options)}` : ''

  routerPush(`${url}${query}`)
}

export const startExternalGame =
  (
    game: Partial<GameProduct> & Pick<GameProduct, 'code' | 'type'>,
    routerPush: ReturnType<typeof useRouter>['push'],
    options?: { [key: string]: string | number },
  ): TypedThunk =>
  (dispatch, getState) => {
    const activeCurrency = getActiveCurrencyIdSelector(getState())
    setDataToSessionStorage(INTERNAL_GAME_NAVIGATION, 'true')

    if (game.type === 'bingo') {
      openBingoGame(activeCurrency, routerPush, game.code === 'bingo' ? '' : game.route, options)
    } else {
      openDialog({
        dialogName: 'START_GAME_FLOW',
        dialogProps: {
          game,
          ...options,
        },
      })
    }
  }

export const goToPlayGame =
  (
    game: GameProductShort,
    activeCurrency: SupportedCurrencies,
    routerPush: (path: string, callback?: () => void) => void,
    options?: { [key: string]: string | number },
  ): TypedThunk =>
  (dispatch) => {
    const smBreakpoint = 927
    const isMobile = window.innerWidth < smBreakpoint

    const isTableGame = game?.tags?.includes('table')
    const isSlingo = game?.tags?.includes('SLI')
    // ToDo needs to add 'in_house' tag to @Patrianna TagsType
    // @ts-ignore
    const isArcadeGame = game?.type === 'in_house'

    const rootUrl = () => {
      if (isArcadeGame) {
        return 'casual-games'
      } else if (isSlingo) {
        return 'slingo'
      } else if (isTableGame) {
        return 'table-games'
      } else {
        return 'slots'
      }
    }

    cleanDialogStack()

    setDataToSessionStorage(INTERNAL_GAME_NAVIGATION, 'true')

    const url = `/${rootUrl()}/${game.route}/${activeCurrency === 'GC' ? '' : 'sweepstake/'}play/${
      isMobile ? 'mobile/' : ''
    }`
    const query = options && Object.keys(options).length > 0 ? `?${stringify(options)}` : ''

    routerPush(`${url}${query}`, () => {
      dispatch(setActiveCurrency({ activeCurrencyId: activeCurrency }))
    })
  }

export const openFirstSlotGame =
  (location: string, routerPush: ReturnType<typeof useRouter>['push']): TypedThunk =>
  (dispatch, getState) => {
    const isStartGameFlowOpened = getDialogVisibilityByName('START_GAME_FLOW')

    if (location === ROUTES.LANDING_PAGE && !isStartGameFlowOpened) {
      const firstGame = getFirstUnlockedSlotSelector(getState())
      if (firstGame) {
        dispatch(startExternalGame(firstGame, routerPush))
      }
    }
  }

export const startExternalGameFlow =
  (
    game: GameProductShort,
    activeCurrency: SupportedCurrencies,
    routerPush: (path: string, callback?: () => void) => void,
    options?: { [key: string]: string | number },
  ): TypedThunk =>
  (dispatch, getState) => {
    const isGuest = isGuestModeSelector(getState())
    const isLoggedIn = isLoggedInSelector(getState())
    const skipOtp = skipOtpSelectorWithMandatoryAndClosed(getState())
    const isOTPrequired = isLoggedIn && !skipOtp && activeCurrency === 'SC'
    const userRestrictions = getUserRestrictionsSelector(getState())

    if (!isLoggedIn) {
      cleanDialogStack()
      routerPush(ROUTES.LOGIN)
    } else if (isGuest) {
      openDialog({ dialogName: 'SCPR_AUTH_WAYS_DIALOG' })
    } else if (isOTPrequired) {
      cleanDialogStack()
      openDialog({ dialogName: 'PHONE_VERIFICATION_DIALOG' })
    } else if (userRestrictions.includes('no_game')) {
      cleanDialogStack()
      openDialog({ dialogName: 'RESTRICT_USER_DIALOG' })
    } else {
      dispatch(goToPlayGame(game, activeCurrency, routerPush, options))
    }
  }

export const showLowOnCoinsDialog =
  (currencyName: SupportedCurrencies): TypedThunk =>
  (dispatch, getState) => {
    const isOfferAvaialble = isDiscountOffersAvailable(getState())
    const activeCurrency = currencyName ? 'SC' : 'GC'
    const dialogName = activeCurrency === 'SC' ? 'NOT_ENOUGH_FUNDS' : 'LOW_COINS_DIALOG'

    trackEvent('low_on_funds', { category: 'Low on funds', label: activeCurrency })

    replaceDialog({ dialogName })
    dispatch(setLowOnCoinsLastDateOpened({ lowOnCoinsLastDateOpened: Date.now() }))

    if (isOfferAvaialble && activeCurrency === 'SC') {
      replaceDialog({ dialogName: 'EXTRA_GOLD_DIALOG' })
    }

    return null
  }

export const cleanUpGame = (): TypedThunk => (dispatch, getState) => {
  const isScEnabled = sweepstakeEnabledSelector(getState())
  const sessionTimerId = getItemFormLocalStorageSelector(getState(), 'WELCOME_EMAIL_DIALOG_TIMER_ID')

  dispatch(removeDataFromLocalStorage('WELCOME_EMAIL_DIALOG_TIMER_ID'))
  removeShownSessionDialog('WELCOME_EMAIL_SEND_DIALOG')
  clearTimeout(sessionTimerId)

  dispatch(getAccountBonusesRequest())
  dispatch(getOffers())
  dispatch(getUserFreeSpins(isScEnabled))
}

export const getUserFreeSpins =
  (isScEnabled: boolean): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: GetAccountFreeSpinsRequest = {
      type: 'GetAccountFreeSpinsRequest',
      currency: isScEnabled ? ['SC', 'GC'] : ['GC'],
    }

    gateway
      .emit<GetAccountFreeSpinsResponse>(data)
      .then((respBody) => {
        dispatch(saveFreeSpins({ freeSpins: respBody?.data }))
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
      .finally(() => {
        dispatch(setFreeSpinsLoaded())
      })
  }

export const playInfiniteGames =
  (
    currencyName: string,
    routerPush: (path: string, callback?: () => void) => void,
    options?: { [key: string]: string | number },
  ): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const activeCurrency = currencyName ? 'SC' : 'GC'
    const data: LowOnCoinsRequest = {
      type: 'LowOnCoinsRequest',
    }

    gateway
      .emit<LowOnCoinsResponse>(data)
      .then((body) => {
        if (body.route) {
          getGameByRoute(body.route).then((_game) => {
            if (isGameProductErrorResponse(_game)) {
              throw { status: _game }
            }
            dispatch(setActiveGameCode({ code: _game.code }))
            dispatch(setGoldMinBet({ goldMinBet: _game.goldMinBet }))
            dispatch(setSweepstakeMinBet({ sweepstakeMinBet: _game.sweepstakeMinBet }))
            dispatch(goToPlayGame(_game, activeCurrency, routerPush, options))
          })
          closeLatestDialog()
        }
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
  }

export const playFreeRoom =
  (
    currencyName: string,
    routerPush: ReturnType<typeof useRouter>['push'],
    options?: { [key: string]: string | number },
  ): TypedThunk =>
  () => {
    const activeCurrency = currencyName ? 'SC' : 'GC'

    openBingoGame(activeCurrency, routerPush, 'gc_route_66', options)
    closeLatestDialog()
  }

const getProcessedFreeSpinIds = (): Set<string> => {
  if (typeof window === 'undefined') {
    return new Set<string>()
  }

  try {
    const stored = localStorage.getItem('processedFreeSpinIds')

    return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>()
  } catch (e) {
    console.error(e)

    return new Set<string>()
  }
}

const saveProcessedFreeSpinIds = (ids: Set<string>): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.setItem('processedFreeSpinIds', JSON.stringify([...ids]))
  } catch (e) {
    console.error('Error on save processedFreeSpinIds in localStorage:', e)
  }
}

const processedFreeSpinIds = getProcessedFreeSpinIds()

export const showFSModalFlow =
  (freeSpins: FreeSpinType[]): TypedThunk =>
  (dispatch, getState) => {
    if (!freeSpins.length) {
      return
    }

    const storedProcessedFreeSpinIds = getItemFormLocalStorageSelector(getState(), PROCESSED_FREE_SPIN_IDS, false)
    const nextProcessedFreeSpinIds = Array.isArray(storedProcessedFreeSpinIds)
      ? new Set<string>(storedProcessedFreeSpinIds.filter((id): id is string => typeof id === 'string'))
      : new Set<string>()

    const wasFSDialogShown = getShownSessionDialogs()?.includes('FREE_SPINS_DIALOG')
    const hasAnyDialogOpen = !!getDialogStack()?.length

    if (wasFSDialogShown && !hasAnyDialogOpen) {
      freeSpins.forEach((fs) => nextProcessedFreeSpinIds.add(fs.campaignBonusCode))

      dispatch(setDataToLocalStorage(PROCESSED_FREE_SPIN_IDS, [...nextProcessedFreeSpinIds], false))
      openDialog({ dialogName: 'FREE_SPINS_DIALOG', dialogProps: { freeSpins } })
    } else if (wasFSDialogShown && hasAnyDialogOpen) {
      dispatch(saveFreeSpins({ freeSpins }))

      removeShownSessionDialog('FREE_SPINS_DIALOG')
    } else {
      dispatch(saveFreeSpins({ freeSpins }))
    }
  }
